import type { GitlabComplianceReason } from '../lib/gitlab-api';

/**
 * The GitLab compliance gates (docs/specs/03-gitlab-automation §C), shown as a
 * checklist. Order is presentation-only; `gate` matches the backend gate number
 * so a failed gate (from the callback's `reasons[]`) renders ✗ with its fix-it
 * message, and every other gate renders ✓.
 *
 * Note: on the "connected" card we render with no reasons → all ✓ (a connected
 * account passed every gate at connect time).
 */
const GATES: { gate: number; label: string }[] = [
  { gate: 2, label: 'Account created with Google sign-in (@optisigns.com)' },
  { gate: 1, label: 'Two-factor authentication (2FA) enabled' },
  { gate: 3, label: 'GitLab account is active' },
  { gate: 4, label: 'GitLab email matches your OptiSigns identity (@optisigns.com / @alphasphere.io)' },
];

export function ComplianceChecklist({ reasons }: { reasons?: GitlabComplianceReason[] }) {
  const failed = new Map((reasons ?? []).map((r) => [r.gate, r.message]));
  return (
    <ul className="space-y-1.5" data-testid="gitlab-checklist">
      {GATES.map(({ gate, label }) => {
        const ok = !failed.has(gate);
        return (
          <li key={gate} className="flex items-start gap-2 text-sm">
            <span
              className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                ok ? 'bg-success-soft text-success' : 'bg-destructive-soft text-destructive'
              }`}
              aria-hidden
            >
              {ok ? '✓' : '✗'}
            </span>
            <span className={ok ? 'text-foreground' : 'text-destructive'}>
              {label}
              {!ok ? (
                <span className="mt-0.5 block text-xs text-destructive">{failed.get(gate)}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
